// Custom Hooks for PracticeTest Component
import type { Question } from '../../types';
// Extracted to reduce main file size and prevent Cursor crashes

import { useCallback, useEffect, useMemo, useState } from 'react';
import { PracticeTestState } from './PracticeTest.reducer';
import {
  TEST_METADATA,
  createGetTranslatedTestNameForId,
  type GetNextTestParams,
  type TopicScore,
} from './PracticeTest.utils';
import { aiCoach } from '../../services/aiCoach';
import { userAuth } from '../../services/userAuth';
import {
  getAllTopicPerformance,
  getCompletedTestCount,
} from '../../services/topicPerformanceService';

// Hook for updating questions (no-op when session is frozen to prevent mid-test mutations).
// Practice only: payload is { questions, isPractice: true }; exam uses raw array.
export const useUpdateQuestions = (
  setQuestions: React.Dispatch<React.SetStateAction<Question[]>>,
  dispatch: React.Dispatch<any>,
  sessionFrozenRef?: React.MutableRefObject<boolean>,
  testId?: string
) => {
  const payload = useCallback((q: Question[]) =>
    testId !== 'mock-exam' ? { questions: q, isPractice: true as const } : q, [testId]);
  return useCallback((newQuestions: Question[] | ((prev: Question[]) => Question[])) => {
    if (sessionFrozenRef?.current) return;
    if (typeof newQuestions === 'function') {
      setQuestions(prev => {
        if (sessionFrozenRef?.current) return prev;
        const updated = newQuestions(prev);
        dispatch({ type: 'SET_QUESTIONS', payload: payload(updated) });
        return updated;
      });
    } else {
      setQuestions(newQuestions);
      dispatch({ type: 'SET_QUESTIONS', payload: payload(newQuestions) });
    }
  }, [setQuestions, dispatch, sessionFrozenRef, payload]);
};

/**
 * Fetches Supabase topic performance for Netflix recommendation engine.
 * Returns GetNextTestParams when userId exists; null for guests.
 */
export const useSupabaseRecommendationContext = (
  testId: string | undefined,
  testComplete?: boolean,
  currentScore?: number,
  currentTotal?: number
): GetNextTestParams | null => {
  const [context, setContext] = useState<GetNextTestParams | null>(null);

  useEffect(() => {
    const userId = userAuth.getCurrentUser()?.id || localStorage.getItem('userId') || null;
    if (!userId || !testId || testId === 'mock-exam' || testId.includes('mock')) {
      setContext(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const [rows, completedCount] = await Promise.all([
          getAllTopicPerformance(userId),
          getCompletedTestCount(userId),
        ]);

        if (cancelled) return;

        const topicScores: TopicScore[] = rows.map((r) => ({
          topicId: r.topic_id,
          accuracy: Number(r.accuracy),
        }));

        let completedTopics = Array.from(new Set(topicScores.map((t) => t.topicId)));
        if (testId && !completedTopics.includes(testId)) {
          completedTopics = [...completedTopics, testId];
        }

        let effectiveCompletedCount = completedTopics.length;
        if (testComplete && currentScore !== undefined && currentTotal !== undefined && currentTotal > 0) {
          const currentAccuracy = Math.round((currentScore / currentTotal) * 100);
          const existing = topicScores.find((t) => t.topicId === testId);
          if (!existing) {
            topicScores.push({ topicId: testId, accuracy: currentAccuracy });
          } else if (currentAccuracy > existing.accuracy) {
            const idx = topicScores.findIndex((t) => t.topicId === testId);
            if (idx >= 0) topicScores[idx] = { topicId: testId, accuracy: currentAccuracy };
          }
        } else {
          effectiveCompletedCount = Math.max(effectiveCompletedCount, completedCount);
        }

        setContext({
          effectiveCompletedCount,
          topicScores,
          completedTopics,
        });
      } catch {
        if (!cancelled) setContext(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [testId, testComplete, currentScore, currentTotal]);

  return context;
};

// Hook for getting translated test name
export const useGetTranslatedTestNameForId = (
  currentLanguage: string,
  t_nested: (key: string) => string
) => {
  const getTranslatedTestNameForId = useMemo(() => {
    return createGetTranslatedTestNameForId(currentLanguage, t_nested);
  }, [currentLanguage, t_nested]);
  
  return useCallback((testIdToTranslate: string): string => {
    return getTranslatedTestNameForId(testIdToTranslate);
  }, [getTranslatedTestNameForId]);
};

// Hook for memoized values
export const useMemoizedValues = (
  questions: Question[],
  state: PracticeTestState
) => {
  const progress = useMemo(() => {
    return questions.length > 0 ? ((state.currentQuestionIndex + 1) / questions.length) * 100 : 0;
  }, [questions.length, state.currentQuestionIndex]);
  
  const questionSubject = useMemo(() => {
    const len = questions.length;
    if (len === 0) return '';
    const safeIndex = Math.min(state.currentQuestionIndex, len - 1);
    return questions[safeIndex]?.subject || '';
  }, [questions, state.currentQuestionIndex]);
  
  const percentage = useMemo(() => {
    return state.testComplete && questions.length > 0 
      ? Math.round((state.score / questions.length) * 100) 
      : 0;
  }, [state.testComplete, state.score, questions.length]);
  
  // Note: nextTest calculation requires testId and other params, so it's handled in main component
  const _nextTest = useMemo(() => {
    return { id: '', name: '' };
  }, []);
  
  const testScores = state.testComplete ? aiCoach.getTestScores() : {};
  const allTestIds = Object.keys(TEST_METADATA);
  const completedTestIds = Object.keys(testScores);
  
  // Check if all tests are completed with ≥60% score
  const allTestsCompletedWith60Percent = state.testComplete && allTestIds.length > 0 && 
    allTestIds.every(testId => {
      const testData = testScores[testId];
      return testData && testData.average >= 60;
    });
  
  // Progress indicator: completed tests / total tests
  const completedTestsCount = completedTestIds.length;
  const totalTestsCount = allTestIds.length;
  const _progressPercentage = totalTestsCount > 0 ? Math.round((completedTestsCount / totalTestsCount) * 100) : 0;
  
  const isAIExamUnlocked = useMemo(() => {
    if (!state.testComplete) return false;
    return aiCoach.canUnlockAIExam();
  }, [state.testComplete]);
  
  return {
    progress,
    questionSubject,
    percentage,
    // Note: nextTest, resultsPageCTAs calculated in main component with proper params
    isAIExamUnlocked,
    allTestsCompletedWith60Percent,
    // Legacy helpers
    isCritical: state.testComplete && percentage < 60,
    hasMastery: state.testComplete && percentage >= 80
  };
};
